<template>
  <div class="tenants-page">

    <!-- Banner: tenant sendo visualizado -->
    <b-alert show variant="info" class="mb-2">
      <div class="d-flex align-items-center">
        <feather-icon icon="GlobeIcon" size="18" class="mr-1" />
        <span>
          Visualizando dados de: <strong>{{ tenantVisualizado }}</strong>
          <b-badge v-if="tenantVisualizado !== 'dkp'" variant="warning" class="ml-1">Impersonação ativa</b-badge>
        </span>
        <b-button
          v-if="tenantVisualizado !== 'dkp'"
          size="sm"
          variant="outline-primary"
          class="ml-auto"
          @click="voltarTenantPadrao"
        >
          <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
          Voltar ao padrão (dkp)
        </b-button>
      </div>
    </b-alert>

    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center mb-1 mb-md-0">
            <h4 class="mb-0">
              <feather-icon icon="LayersIcon" size="20" class="mr-75" />
              Gestão de Tenants (Clientes)
            </h4>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end" style="gap: 8px">
              <b-form-input
                v-model="filtro"
                placeholder="Filtrar por nome ou slug..."
                style="max-width: 260px"
              />
              <b-button variant="primary" @click="abrirModalCriar">
                <feather-icon icon="PlusIcon" size="14" class="mr-50" />
                Novo Tenant
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-overlay :show="carregando">
        <b-table
          :items="tenantsFiltrados"
          :fields="colunas"
          responsive
          show-empty
          empty-text="Nenhum tenant cadastrado"
          class="position-relative"
          striped
          hover
        >
          <template #cell(ativo)="data">
            <b-badge :variant="data.value ? 'success' : 'danger'">
              {{ data.value ? 'Ativo' : 'Inativo' }}
            </b-badge>
          </template>

          <template #cell(plano)="data">
            <b-badge :variant="corPlano(data.value)">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(data_criacao)="data">
            {{ formatarData(data.value) }}
          </template>

          <template #cell(acoes)="data">
            <div class="d-flex" style="gap: 6px">
              <b-button
                size="sm"
                variant="gradient-success"
                title="Acessar dados deste tenant"
                @click="acessarComoTenant(data.item)"
              >
                <feather-icon icon="LogInIcon" size="13" />
              </b-button>
              <b-button
                size="sm"
                variant="gradient-primary"
                title="Editar"
                @click="abrirModalEditar(data.item)"
              >
                <feather-icon icon="EditIcon" size="13" />
              </b-button>
              <b-button
                size="sm"
                variant="gradient-info"
                title="Testar conexão com o banco"
                :disabled="testando === data.item.id"
                @click="testarConexao(data.item)"
              >
                <b-spinner v-if="testando === data.item.id" small />
                <feather-icon v-else icon="ZapIcon" size="13" />
              </b-button>
              <b-button
                size="sm"
                variant="gradient-warning"
                title="Provisionar banco (criar tabelas ausentes)"
                :disabled="provisionando === data.item.id"
                @click="provisionarBanco(data.item)"
              >
                <b-spinner v-if="provisionando === data.item.id" small />
                <feather-icon v-else icon="DatabaseIcon" size="13" />
              </b-button>
              <b-button
                size="sm"
                :variant="data.item.ativo ? 'gradient-danger' : 'gradient-warning'"
                :title="data.item.ativo ? 'Desativar' : 'Reativar'"
                @click="confirmarToggle(data.item)"
              >
                <feather-icon :icon="data.item.ativo ? 'PowerIcon' : 'RefreshCwIcon'" size="13" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-overlay>

      <div class="mx-2 mb-2">
        <small class="text-muted">
          {{ tenantsFiltrados.length }} tenant(s) exibidos de {{ tenants.length }} total
        </small>
      </div>
    </b-card>

    <!-- Modal: Criar Tenant -->
    <b-modal
      v-model="modalCriar"
      title="Novo Tenant (Cliente)"
      size="lg"
      ok-title="Criar"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="salvando"
      @ok.prevent="criarTenant"
      @hidden="resetarFormulario"
    >
      <b-overlay :show="salvando">
        <b-form @submit.prevent="criarTenant">
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Nome da Empresa *">
                <b-form-input v-model="form.nome" placeholder="Ex: Empresa ABC" required />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Slug (identificador único) *">
                <b-form-input
                  v-model="form.slug"
                  placeholder="ex: empresa-abc"
                  required
                  @input="form.slug = slugify(form.slug)"
                />
                <small class="text-muted">Usado na URL e no header x-tenant-slug</small>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <hr /><h6 class="text-primary">Conexão com o Banco de Dados</h6>
            </b-col>

            <b-col cols="12" md="8">
              <b-form-group label="Host do Banco *">
                <b-form-input v-model="form.db_host" placeholder="Ex: rds.amazonaws.com" required />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <b-form-group label="Porta">
                <b-form-input v-model="form.db_port" placeholder="3306" type="number" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Nome do Banco *">
                <b-form-input v-model="form.db_name" placeholder="Ex: db_cliente_abc" required />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Usuário do Banco *">
                <b-form-input v-model="form.db_user" placeholder="Ex: admin" required />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Senha do Banco *">
                <b-form-input
                  v-model="form.db_password"
                  type="password"
                  placeholder="Senha do banco"
                  required
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <hr /><h6 class="text-primary">Configurações do Plano</h6>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group label="Plano">
                <b-form-select v-model="form.plano" :options="opcoesPlano" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Máx. Usuários">
                <b-form-input v-model="form.max_usuarios" type="number" placeholder="10" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Cor Primária">
                <b-form-input v-model="form.cor_primaria" type="color" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Data de Expiração (opcional)">
                <b-form-datepicker v-model="form.data_expiracao" locale="pt-BR" />
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-overlay>
    </b-modal>

    <!-- Modal: Editar Tenant -->
    <b-modal
      v-model="modalEditar"
      :title="`Editar: ${tenantEditando ? tenantEditando.nome : ''}`"
      size="lg"
      ok-title="Salvar"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="salvando"
      @ok.prevent="salvarEdicao"
      @hidden="tenantEditando = null"
    >
      <b-overlay :show="salvando" v-if="tenantEditando">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Nome da Empresa">
              <b-form-input v-model="tenantEditando.nome" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Slug">
              <b-form-input v-model="tenantEditando.slug" disabled />
              <small class="text-muted">O slug não pode ser alterado</small>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Plano">
              <b-form-select v-model="tenantEditando.plano" :options="opcoesPlano" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Máx. Usuários">
              <b-form-input v-model="tenantEditando.max_usuarios" type="number" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Cor Primária">
              <b-form-input v-model="tenantEditando.cor_primaria" type="color" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Domínio Customizado">
              <b-form-input v-model="tenantEditando.dominio_customizado" placeholder="cliente.dominio.com" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Logo (URL)">
              <b-form-input v-model="tenantEditando.logo_url" placeholder="https://..." />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Data de Expiração">
              <b-form-datepicker v-model="tenantEditando.data_expiracao" locale="pt-BR" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Status">
              <b-form-checkbox v-model="tenantEditando.ativo" switch>
                {{ tenantEditando.ativo ? 'Ativo' : 'Inativo' }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
      </b-overlay>
    </b-modal>

    <!-- Modal: Confirmar acesso como tenant -->
    <b-modal
      v-model="modalAcesso"
      :title="`Visualizar dados de: ${tenantParaAcesso ? tenantParaAcesso.nome : ''}`"
      ok-title="Confirmar"
      cancel-title="Cancelar"
      ok-variant="success"
      @ok="confirmarAcesso"
    >
      <p>
        Você irá visualizar os dados do tenant
        <strong>{{ tenantParaAcesso ? tenantParaAcesso.nome : '' }}</strong>
        (slug: <code>{{ tenantParaAcesso ? tenantParaAcesso.slug : '' }}</code>).
      </p>
      <p class="text-info">
        <feather-icon icon="InfoIcon" />
        Sua sessão permanece autenticada como AdminDKP. Apenas o contexto de dados muda.
        Clique em "Voltar ao padrão (dkp)" para encerrar a visualização.
      </p>
    </b-modal>

  </div>
</template>

<script>
import TenantService from '@/services/tenant.service';
import { setImpersonateTenant, clearImpersonation, getImpersonateTenant } from '@/services/tenant';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import {
  BCard, BRow, BCol, BTable, BBadge, BButton, BOverlay,
  BFormInput, BForm, BFormGroup, BFormSelect, BFormDatepicker,
  BFormCheckbox, BModal, BAlert, BSpinner, BInputGroup,
} from 'bootstrap-vue';
import Ripple from 'vue-ripple-directive';

export default {
  name: 'TenantsList',
  components: {
    BCard, BRow, BCol, BTable, BBadge, BButton, BOverlay,
    BFormInput, BForm, BFormGroup, BFormSelect, BFormDatepicker,
    BFormCheckbox, BModal, BAlert, BSpinner, BInputGroup,
  },
  directives: { Ripple },

  data() {
    return {
      tenants: [],
      filtro: '',
      carregando: false,
      salvando: false,
      testando: null,
      provisionando: null,

      modalCriar: false,
      modalEditar: false,
      modalAcesso: false,
      tenantEditando: null,
      tenantParaAcesso: null,

      colunas: [
        { key: 'id', label: 'ID', sortable: true, thStyle: { width: '60px' } },
        { key: 'nome', label: 'Empresa', sortable: true },
        { key: 'slug', label: 'Slug', sortable: true },
        { key: 'db_name', label: 'Banco', sortable: true },
        { key: 'plano', label: 'Plano', sortable: true },
        { key: 'max_usuarios', label: 'Máx. Users', sortable: true },
        { key: 'ativo', label: 'Status', sortable: true },
        { key: 'data_criacao', label: 'Criado em', sortable: true },
        { key: 'acoes', label: 'Ações', thStyle: { width: '200px' } },
      ],

      form: {
        nome: '',
        slug: '',
        db_host: '',
        db_port: '3306',
        db_name: '',
        db_user: '',
        db_password: '',
        plano: 'starter',
        max_usuarios: 10,
        cor_primaria: '#1976D2',
        data_expiracao: null,
      },

      opcoesPlano: [
        { value: 'starter', text: 'Starter' },
        { value: 'business', text: 'Business' },
        { value: 'enterprise', text: 'Enterprise' },
      ],
    };
  },

  computed: {
    tenantVisualizado() {
      return getImpersonateTenant() || localStorage.getItem('tenantSlug') || 'dkp';
    },
    tenantsFiltrados() {
      if (!this.filtro) return this.tenants;
      const f = this.filtro.toLowerCase();
      return this.tenants.filter(
        t => t.nome?.toLowerCase().includes(f) || t.slug?.toLowerCase().includes(f)
      );
    },
  },

  created() {
    // Migração: se o mecanismo antigo deixou tenantSlug ≠ 'dkp' sem impersonação ativa,
    // reseta para 'dkp' — esta página é exclusiva do AdminDKP e auth sempre usa DKP.
    const impersonateSlug = localStorage.getItem('dkpImpersonateSlug');
    const tenantSlug = localStorage.getItem('tenantSlug');
    if (!impersonateSlug && tenantSlug && tenantSlug !== 'dkp') {
      localStorage.setItem('tenantSlug', 'dkp');
    }
    this.carregarTenants();
  },

  methods: {
    toast(title, text, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, text, icon, variant },
      });
    },

    async carregarTenants() {
      this.carregando = true;
      try {
        this.tenants = await TenantService.listar();
      } catch (err) {
        this.toast('Erro', 'Erro ao carregar tenants: ' + err.message, 'AlertCircleIcon', 'danger');
      } finally {
        this.carregando = false;
      }
    },

    abrirModalCriar() {
      this.resetarFormulario();
      this.modalCriar = true;
    },

    abrirModalEditar(tenant) {
      this.tenantEditando = { ...tenant };
      this.modalEditar = true;
    },

    async criarTenant() {
      this.salvando = true;
      try {
        await TenantService.criar(this.form);
        this.toast('Sucesso', `Tenant "${this.form.nome}" criado com sucesso!`, 'CheckCircleIcon', 'success');
        this.modalCriar = false;
        await this.carregarTenants();
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.toast('Erro', 'Erro ao criar tenant: ' + msg, 'AlertCircleIcon', 'danger');
      } finally {
        this.salvando = false;
      }
    },

    async salvarEdicao() {
      this.salvando = true;
      try {
        await TenantService.atualizar(this.tenantEditando.id, this.tenantEditando);
        this.toast('Sucesso', 'Tenant atualizado com sucesso!', 'CheckCircleIcon', 'success');
        this.modalEditar = false;
        await this.carregarTenants();
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.toast('Erro', 'Erro ao salvar: ' + msg, 'AlertCircleIcon', 'danger');
      } finally {
        this.salvando = false;
      }
    },

    async provisionarBanco(tenant) {
      this.provisionando = tenant.id;
      try {
        const resultado = await TenantService.provisionar(tenant.id);
        const det = resultado.detalhes;
        const temErros = det.erros?.length > 0;
        this.toast(
          temErros ? 'Provisionamento com avisos' : 'Provisionamento Concluído',
          `${resultado.message}${temErros ? ` (${det.erros.length} tabela(s) com erro)` : ''}`,
          temErros ? 'AlertTriangleIcon' : 'DatabaseIcon',
          temErros ? 'warning' : 'success',
        );
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.toast('Falha no Provisionamento', msg, 'AlertCircleIcon', 'danger');
      } finally {
        this.provisionando = null;
      }
    },

    async testarConexao(tenant) {
      this.testando = tenant.id;
      try {
        const resultado = await TenantService.testarConexao(tenant.id);
        this.toast(
          resultado.ok ? 'Conexão OK' : 'Falha na conexão',
          resultado.message,
          resultado.ok ? 'ZapIcon' : 'AlertCircleIcon',
          resultado.ok ? 'success' : 'danger',
        );
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.toast('Erro de Conexão', msg, 'AlertCircleIcon', 'danger');
      } finally {
        this.testando = null;
      }
    },

    confirmarToggle(tenant) {
      const acao = tenant.ativo ? 'desativar' : 'reativar';
      this.$bvModal.msgBoxConfirm(`Deseja ${acao} o tenant "${tenant.nome}"?`, {
        title: 'Confirmar',
        okTitle: acao.charAt(0).toUpperCase() + acao.slice(1),
        cancelTitle: 'Cancelar',
        okVariant: tenant.ativo ? 'danger' : 'success',
      }).then(async (confirmado) => {
        if (!confirmado) return;
        try {
          await TenantService.atualizar(tenant.id, { ativo: !tenant.ativo });
          await this.carregarTenants();
          this.toast('Sucesso', `Tenant ${acao + 'd'}o com sucesso.`, 'CheckCircleIcon', 'success');
        } catch (err) {
          this.toast('Erro', err.message, 'AlertCircleIcon', 'danger');
        }
      });
    },

    acessarComoTenant(tenant) {
      this.tenantParaAcesso = tenant;
      this.modalAcesso = true;
    },

    confirmarAcesso() {
      // Garante que o tenantSlug de autenticação seja sempre 'dkp'
      localStorage.setItem('tenantSlug', 'dkp');
      setImpersonateTenant(this.tenantParaAcesso.slug);
      this.toast(
        'Contexto alterado',
        `Visualizando dados de "${this.tenantParaAcesso.nome}". Sua sessão continua como AdminDKP.`,
        'GlobeIcon',
        'warning',
      );
      this.modalAcesso = false;
    },

    voltarTenantPadrao() {
      clearImpersonation();
      localStorage.setItem('tenantSlug', 'dkp');
      this.toast('Contexto restaurado', 'Voltando ao tenant padrão (dkp).', 'HomeIcon', 'info');
    },

    resetarFormulario() {
      this.form = {
        nome: '', slug: '', db_host: '', db_port: '3306',
        db_name: '', db_user: '', db_password: '',
        plano: 'starter', max_usuarios: 10,
        cor_primaria: '#1976D2', data_expiracao: null,
      };
    },

    slugify(str) {
      return str
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
    },

    corPlano(plano) {
      const map = { starter: 'secondary', business: 'primary', enterprise: 'warning' };
      return map[plano] || 'secondary';
    },

    formatarData(data) {
      if (!data) return '-';
      return new Date(data).toLocaleDateString('pt-BR');
    },
  },
};
</script>

<style scoped>
.tenants-page {
  padding: 0;
}
</style>
